import type { GiantId } from '../../domain/ids/ids';
import { getAudioService } from '../../infrastructure';
import {
  LEVEL_MUSIC_PLAYLIST,
  LOOPING_SOUND_IDS,
  SINGLE_INSTANCE_SOUND_IDS,
  soundUrls,
  type SoundId,
} from './soundCatalog';

const VINYL_MUSIC_SOUND_ID: SoundId = 'music.tech_like';
const MAP_MUSIC_SOUND_ID: SoundId = 'music.map';

export type BackgroundMusicMode = 'map' | 'level';

let desiredBackgroundMusic: BackgroundMusicMode | null = null;
let backgroundMusicSuspended = false;
let levelMusicTrackIndex = 0;
let pickRandomLevelTrackOnNextPlay = true;
let levelMusicEndHandlersBound = false;

function isLevelMusicPlaying(): boolean {
  const audio = getAudioService();
  return LEVEL_MUSIC_PLAYLIST.some((id) => audio.isPlaying(id));
}

function stopLevelMusic(): void {
  const audio = getAudioService();
  for (const id of LEVEL_MUSIC_PLAYLIST) {
    audio.stop(id);
  }
}

function stopAllBackgroundMusic(): void {
  stopLevelMusic();
  getAudioService().stop(MAP_MUSIC_SOUND_ID);
}

function advanceLevelMusicTrack(): void {
  levelMusicTrackIndex =
    (levelMusicTrackIndex + 1) % LEVEL_MUSIC_PLAYLIST.length;
}

function pickRandomLevelMusicTrack(): void {
  levelMusicTrackIndex = Math.floor(
    Math.random() * LEVEL_MUSIC_PLAYLIST.length,
  );
}

function onLevelMusicTrackEnded(): void {
  if (desiredBackgroundMusic !== 'level' || backgroundMusicSuspended) {
    return;
  }
  advanceLevelMusicTrack();
  pickRandomLevelTrackOnNextPlay = false;
  playDesiredBackgroundMusic();
}

function ensureLevelMusicEndHandlers(): void {
  if (levelMusicEndHandlersBound) {
    return;
  }
  levelMusicEndHandlersBound = true;
  const audio = getAudioService();
  for (const id of LEVEL_MUSIC_PLAYLIST) {
    audio.onEnd(id, onLevelMusicTrackEnded);
  }
}

function playDesiredBackgroundMusic(): void {
  if (desiredBackgroundMusic == null || backgroundMusicSuspended) {
    return;
  }
  const audio = getAudioService();

  if (desiredBackgroundMusic === 'map') {
    stopLevelMusic();
    if (audio.isPlaying(MAP_MUSIC_SOUND_ID)) {
      return;
    }
    audio.play(MAP_MUSIC_SOUND_ID, { bypassMuteGate: true });
    return;
  }

  audio.stop(MAP_MUSIC_SOUND_ID);
  if (isLevelMusicPlaying()) {
    return;
  }
  if (pickRandomLevelTrackOnNextPlay) {
    pickRandomLevelMusicTrack();
    pickRandomLevelTrackOnNextPlay = false;
  }
  const id =
    LEVEL_MUSIC_PLAYLIST[levelMusicTrackIndex] ?? LEVEL_MUSIC_PLAYLIST[0];
  audio.play(id, { bypassMuteGate: true });
}

/** Switch BGM between map hub (looped) and level gameplay playlist. */
export function setBackgroundMusic(mode: BackgroundMusicMode): void {
  if (mode === 'level' && desiredBackgroundMusic !== 'level') {
    pickRandomLevelTrackOnNextPlay = true;
  }
  desiredBackgroundMusic = mode;
  if (backgroundMusicSuspended) {
    stopAllBackgroundMusic();
    return;
  }
  playDesiredBackgroundMusic();
}

/** Pause BGM (e.g. while vinyl track plays). */
export function suspendBackgroundMusic(): void {
  backgroundMusicSuspended = true;
  stopAllBackgroundMusic();
}

/** Resume last requested BGM after a temporary suspend. */
export function resumeBackgroundMusic(): void {
  backgroundMusicSuspended = false;
  playDesiredBackgroundMusic();
}

/** Yetti order «Песня для вдохновения» — playable vinyl track. */
export function isVinylMusicDelivery(
  giantId: GiantId,
  orderIndex: number,
): boolean {
  return giantId === 'yetti' && orderIndex === 0;
}

/** Load Howl instances for the sound catalog (soft-fail). */
export function preloadSounds(): void {
  if (process.env.NODE_ENV === 'test') {
    return;
  }
  try {
    getAudioService().loadCatalog(soundUrls, {
      singleInstanceIds: SINGLE_INSTANCE_SOUND_IDS,
      loopIds: LOOPING_SOUND_IDS,
    });
    ensureLevelMusicEndHandlers();
  } catch (e) {
    console.warn('[presentation] sound preload failed', e);
  }
}

/** Play a catalog sound via the shared AudioService. */
export function playSound(id: SoundId, options?: { volume?: number }): void {
  getAudioService().play(id, options);
}

export function playVinylMusic(): void {
  suspendBackgroundMusic();
  getAudioService().play(VINYL_MUSIC_SOUND_ID);
}

export function stopVinylMusic(): void {
  getAudioService().stop(VINYL_MUSIC_SOUND_ID);
  resumeBackgroundMusic();
}

export function toggleVinylMusic(): void {
  const audio = getAudioService();
  if (audio.isPlaying(VINYL_MUSIC_SOUND_ID)) {
    stopVinylMusic();
    return;
  }
  playVinylMusic();
}

function pickVariant(a: SoundId, b: SoundId): SoundId {
  return Math.random() < 0.5 ? a : b;
}

/** Default UI click — arrows, back, generic buttons. */
export function playUiClick(): void {
  playSound('ui.click');
}

/** Hub / map chrome navigation (nav tabs, tickets, rating, player level). */
export function playUiClickB(): void {
  playSound('ui.click_b');
}

export function playUiInfo(): void {
  playSound('ui.info_b');
}

export function playDeliveryTake(): void {
  playSound('delivery.take');
}

export function playDeliverySuccess(): void {
  playSound('delivery.success');
}

export function playDeliveryFail(): void {
  playSound('delivery.fail');
}

export function playRunnerHit(): void {
  playSound(pickVariant('runner.hit', 'runner.hit_b'));
}

export function playRunnerSpark(): void {
  playSound(pickVariant('runner.spark', 'runner.spark_b'));
}

export function playRunnerBooster(): void {
  playSound(pickVariant('runner.booster', 'runner.booster_b'));
}

export function playRunnerFinish(): void {
  playSound('runner.finish');
}

/** Once after the loading screen completes at app start. */
export function playLoadingSfx(): void {
  playSound('loading.sfx');
}
